import { useState } from 'react';
import { Alert, Card, MenuItem, Stack, TextField, Typography } from '@mui/material';

// The text of the "Add bookings to" card, from the design spec sections 9.1, 9.2 and 9.4.
// The events have fixed content: there is no event content choice (D9, D28).

export const DESTINATION_HEADING = 'Add bookings to';

export const NO_DESTINATION = 'Choose a calendar to add your approved bookings to Google.';

// No connection has the write permission. The one Grant button is on the connection card
// (spec 9.2, decided 2026-10-16), so this card points to it and has no button of its own.
export const NEEDS_WRITE_PERMISSION = 'To add bookings to Google, use Grant permission above.';

export const WRITES_OFF = 'Adding bookings to Google is not available yet.';

export const NO_BOOKINGS_OPTION = 'Do not add bookings';

export const WHO_SEES_NOTE =
  "Each event shows your customer's name and email to everyone who can see this calendar.";

export const PENDING_NOTE = 'Bookings waiting for your approval are added when you approve them.';

export const EVENT_CONTENT_NOTE =
  'Each event: "Service: Customer name", no form answers, your Google default reminder.';

export const EDITS_NOTE =
  'Changes you make to these events in Google do not change the booking, and the customer is not told. To move or cancel a booking, do it in FlowBookings. If you move an event in Google, it stays where you put it, and its old time stays booked. If you edit its title or description, FlowBookings writes its own text again the next time it updates the event, for example after a change to the booking, its service or customer name. If you delete an event in Google, FlowBookings does not add it again.';

export const NOT_TICKED_NOTE =
  'Events on this calendar do not block your booking times. Tick it above if they should.';

export const changeNote = (from, to) => `Bookings already added stay in ${from}. New bookings go to ${to}.`;

export const problemNote = (name) => `FlowBookings cannot add events to ${name}. Choose another calendar.`;

// The picker value of a destination that left the calendar list. The view then has no name.
export const UNLISTED_CALENDAR = 'A calendar that is no longer listed';

// The picker value of "Do not add bookings". A calendar value always holds a "/".
const NONE = 'none';
const HEADING_ID = 'calendar-destination-heading';

const keyOf = (connectionId, calendarId) => `${connectionId}/${calendarId}`;

// Every calendar bookings can go to: owned, on a connection with the write permission, and
// not refused by Google. The server works this out as `canWrite` (D29).
function writableCalendars(connections) {
  return connections.flatMap((connection) =>
    (connection.calendars || [])
      .filter((calendar) => calendar.canWrite)
      .map((calendar) => ({
        key: keyOf(connection.id, calendar.id),
        destination: { connection: connection.id, calendarId: calendar.id },
        name: calendar.name,
        label: `${calendar.name} (${connection.accountEmail})`,
      }))
  );
}

// The "Add bookings to" card. `onSave(destination)` sends the choice and answers true when
// the server took it. The page owns `isPickerOpen`, so the prompt can open the picker.
export default function DestinationCard({
  connections,
  settings,
  writesAllowed,
  isSaving,
  isPickerOpen,
  onPickerOpenChange,
  onSave,
}) {
  const [changed, setChanged] = useState(null);

  if (!writesAllowed) {
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        {WRITES_OFF}
      </Alert>
    );
  }

  const options = writableCalendars(connections);
  const canPick = connections.some((connection) => connection.permissions?.eventsOwned);
  const destination = settings?.destination || null;
  const currentKey = destination ? keyOf(destination.connection, destination.calendarId) : NONE;
  const isListed = !destination || options.some((option) => option.key === currentKey);
  const destinationCalendar = destination
    ? connections
        .find((connection) => connection.id === destination.connection)
        ?.calendars?.find((calendar) => calendar.id === destination.calendarId)
    : null;
  const knownName = destination?.name || destinationCalendar?.name || null;
  const destinationName = knownName || 'the calendar you chose';
  // A destination outside the picker list can no longer take events, whether or not the
  // server has set `destinationProblem` yet.
  const hasProblem = Boolean(settings?.destinationProblem) || !isListed;

  const choose = async (event) => {
    const key = event.target.value;
    if (key === currentKey) return;
    const next = options.find((option) => option.key === key) || null;
    const saved = await onSave(next && next.destination);
    if (!saved) return;
    // The note belongs to this change. A destination changed any other way hides it.
    setChanged(destination && next ? { key, text: changeNote(knownName || 'your old calendar', next.name) } : null);
  };

  let intro = null;
  if (!canPick) intro = NEEDS_WRITE_PERMISSION;
  else if (!destination) intro = NO_DESTINATION;

  return (
    <Card sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" id={HEADING_ID}>
        {DESTINATION_HEADING}
      </Typography>
      {intro && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {intro}
        </Typography>
      )}

      <TextField
        select
        fullWidth
        value={currentKey}
        disabled={!canPick || isSaving}
        onChange={choose}
        SelectProps={{
          labelId: HEADING_ID,
          open: isPickerOpen && canPick && !isSaving,
          onOpen: () => onPickerOpenChange(true),
          onClose: () => onPickerOpenChange(false),
          // The menu renders outside the page root, so it carries amp-block itself (R1-L3-002).
          MenuProps: { className: 'amp-block' },
        }}
        sx={{ mt: 2, maxWidth: 480 }}
      >
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        ))}
        {/* A destination that left the list stays shown, so the picker never holds a value it cannot show. */}
        {!isListed && (
          <MenuItem value={currentKey} disabled>
            {knownName || UNLISTED_CALENDAR}
          </MenuItem>
        )}
        <MenuItem value={NONE}>{NO_BOOKINGS_OPTION}</MenuItem>
      </TextField>

      <Stack spacing={1} sx={{ mt: 2 }}>
        {hasProblem && <Alert severity="error">{problemNote(destinationName)}</Alert>}
        {destinationCalendar && !destinationCalendar.ticked && <Alert severity="info">{NOT_TICKED_NOTE}</Alert>}
        {changed?.key === currentKey && <Alert severity="info">{changed.text}</Alert>}
        {[WHO_SEES_NOTE, PENDING_NOTE, EVENT_CONTENT_NOTE, EDITS_NOTE].map((note) => (
          <Typography key={note} variant="body2" sx={{ color: 'text.secondary' }}>
            {note}
          </Typography>
        ))}
      </Stack>
    </Card>
  );
}
